import { useState } from 'react';
import { Loader2, Plus, Trash2, Undo2, UserMinus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useMinisterSearch } from '@/features/events/hooks/useMinisterSearch';
import * as repo from '../data/votingRepository';
import { useVotingMutation } from '../hooks/useVoting';
import type { ElectionState } from '../types';
import { MinisterPhoto } from './MinisterPhoto';

const STATUS_LABEL = { ACTIVE: 'Disponible', ELECTED: 'Elegido', WITHDRAWN: 'Retirado' } as const;

export function CandidatesPanel({ state, canEdit }: { state: ElectionState; canEdit: boolean }) {
  const electionId = state.election.id;
  const { searchQuery, setSearchQuery, ministers, isLoading } = useMinisterSearch();
  const [removing, setRemoving] = useState<string | null>(null);
  const existing = new Set(state.candidates.map((c) => c.minister_id));

  const add = useVotingMutation(electionId, (ministerId: string) =>
    repo.addCandidate(electionId, ministerId, state.candidates.length + 1),
  );
  const setStatus = useVotingMutation(electionId, (a: { id: string; status: 'ACTIVE' | 'WITHDRAWN' }) =>
    repo.setCandidateStatus(a.id, a.status),
  );
  const remove = useVotingMutation(electionId, (id: string) => repo.removeCandidate(id), { success: 'Candidato quitado' });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Candidatos</CardTitle>
        <CardDescription>
          Todos los que la sala acepta como candidatos. Cada ronda usa los que tú elijas de esta lista; puedes
          agregar más en cualquier momento. Quien gana un cargo queda como "Elegido" y no se ofrece de nuevo.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {canEdit && (
          <div className="space-y-2">
            <Input
              placeholder="Buscar ministro por nombre, código ADB o CI…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery.length >= 2 && (
              <div className="max-h-56 overflow-auto rounded-md border">
                {isLoading ? (
                  <div className="flex justify-center p-3"><Loader2 className="size-4 animate-spin" /></div>
                ) : ministers.length === 0 ? (
                  <p className="p-3 text-sm text-muted-foreground">Sin resultados.</p>
                ) : (
                  ministers.map((m) => {
                    const already = existing.has(m.id);
                    return (
                      <button
                        key={m.id}
                        type="button"
                        disabled={already || add.isPending}
                        onClick={() => add.mutate(m.id)}
                        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-muted disabled:opacity-50"
                      >
                        <span>{m.full_name} <span className="text-muted-foreground">· {m.adb_code}</span></span>
                        {already ? <Badge variant="secondary">Ya es candidato</Badge> : <Plus className="size-4" />}
                      </button>
                    );
                  })
                )}
              </div>
            )}
          </div>
        )}

        {state.candidates.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aún no hay candidatos.</p>
        ) : (
          <ul className="divide-y rounded-md border">
            {state.candidates.map((c) => (
              <li key={c.id} className="flex items-center gap-3 p-2.5">
                <MinisterPhoto filename={c.photo_filename} name={c.full_name} className="size-10" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{c.full_name}</p>
                  <p className="text-xs text-muted-foreground">{c.adb_code}</p>
                </div>
                <Badge variant={c.status === 'ACTIVE' ? 'outline' : c.status === 'ELECTED' ? 'default' : 'secondary'}>
                  {STATUS_LABEL[c.status]}
                </Badge>
                {canEdit && c.status !== 'ELECTED' && (
                  <>
                    {c.status === 'ACTIVE' ? (
                      <Button size="icon" variant="ghost" title="Retirar candidato" onClick={() => setStatus.mutate({ id: c.id, status: 'WITHDRAWN' })}>
                        <UserMinus className="size-4" />
                      </Button>
                    ) : (
                      <Button size="icon" variant="ghost" title="Volver a habilitar" onClick={() => setStatus.mutate({ id: c.id, status: 'ACTIVE' })}>
                        <Undo2 className="size-4" />
                      </Button>
                    )}
                    <Button
                      size="icon"
                      variant="ghost"
                      title="Quitar de la lista"
                      disabled={removing === c.id}
                      onClick={() => {
                        setRemoving(c.id);
                        remove.mutate(c.id, { onSettled: () => setRemoving(null) });
                      }}
                    >
                      <Trash2 className="size-4 text-destructive" />
                    </Button>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
